import { useEffect, useState } from "react";
import { LineChart, PieChart } from "@mui/x-charts";
import { Tooltip } from "@mui/material";
import { Oval } from "react-loader-spinner";
import { FiHelpCircle, FiRefreshCw, FiSettings } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import Header from "../../components/header";
import toCurrency from "../../scripts/toCurrency";
import styles from "./css/home.module.css";
import SettingsModal from "./components/settingsModal";
import { getDashboardDetails } from "./services/getDashboardDetails";


const EMPTY_NET_GROWTH = { newThisMonth: 0, churnedThisMonth: 0, netGrowth: 0 };
const EMPTY_BILLING_BREAKDOWN = { TRIALING: 0, NO_PAYMENT: 0, OVERDUE: 0, ACTIVE: 0 };
const EMPTY_TRIAL_CONVERSION = { converted: 0, lost: 0, ongoing: 0, conversionRate: 0 };
const EMPTY_LEADS_SUMMARY = { totalLeads: 0, convertedLeads: 0, leadsThisMonth: 0 };
const EMPTY_ORDERS_VOLUME = { ordersToday: 0, ordersLast7Days: 0 };
const EMPTY_APP_FEEDBACK_SUMMARY = { totalFeedback: 0, goodCount: 0, mediumCount: 0, badCount: 0, positivePercent: 0 };

const BILLING_STATUS_CHART_META = [
  { key: "ACTIVE", label: "Em dia", color: "#2e7d32" },
  { key: "OVERDUE", label: "Inadimplente", color: "#d32f2f" },
  { key: "TRIALING", label: "Em teste", color: "#0288d1" },
  { key: "NO_PAYMENT", label: "Sem pagamentos", color: "#757575" },
];


export default function Home() {
  const router = useNavigate();

  // LOADING CONTROLLER
  const [loading, setLoading] = useState(true);


  // DASHBOARD DATA
  const [activeClients, setActiveClients] = useState(0);
  const [monthlyRevenue, setMonthlyRevenue] = useState(0);
  const [averageTicket, setAverageTicket] = useState(0);
  const [netGrowth, setNetGrowth] = useState(EMPTY_NET_GROWTH);
  const [billingStatusBreakdown, setBillingStatusBreakdown] = useState(EMPTY_BILLING_BREAKDOWN);
  const [atRiskRevenue, setAtRiskRevenue] = useState(0);
  const [revenueHistory, setRevenueHistory] = useState([]);
  const [trialConversion, setTrialConversion] = useState(EMPTY_TRIAL_CONVERSION);
  const [leadsSummary, setLeadsSummary] = useState(EMPTY_LEADS_SUMMARY);
  const [ordersVolume, setOrdersVolume] = useState(EMPTY_ORDERS_VOLUME);
  const [appFeedbackSummary, setAppFeedbackSummary] = useState(EMPTY_APP_FEEDBACK_SUMMARY);

  // MODAL DE CONFIGURAÇÕES
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);

  // ATUALIZAÇÃO MANUAL DO DASHBOARD
  const [refreshing, setRefreshing] = useState(false);



  // BUSCA OS DADOS DO DASHBOARD. `showLoading` controla se os cards devem voltar a exibir o
  // spinner individual (carga inicial) ou se a atualizacao deve ser "silenciosa" (refresh em
  // segundo plano, sem apagar os numeros ja exibidos na tela).
  const fetchDashboardData = async (showLoading) => {
    if (showLoading) setLoading(true);

    try{
      const dd = await getDashboardDetails();

      console.log('dd.netGrowth:', dd.netGrowth, Number(dd.netGrowth.netGrowth) < 0);

      const netGrowth = dd.netGrowth ? {
        ...dd.netGrowth, 
        netGrowth: (Number(dd.netGrowth.netGrowth) < 0 ? 0 : dd.netGrowth.netGrowth)
      } : EMPTY_NET_GROWTH;

      setActiveClients(dd.clientsAmount);
      setMonthlyRevenue(dd.invoicing);
      setAverageTicket(dd.averageTicket);
      setNetGrowth(netGrowth);
      setBillingStatusBreakdown(dd.billingStatusBreakdown ?? EMPTY_BILLING_BREAKDOWN);
      setAtRiskRevenue(dd.atRiskRevenue ?? 0);
      setRevenueHistory(dd.revenueHistory ?? []);
      setTrialConversion(dd.trialConversion ?? EMPTY_TRIAL_CONVERSION);
      setLeadsSummary(dd.leadsSummary ?? EMPTY_LEADS_SUMMARY);
      setOrdersVolume(dd.ordersVolume ?? EMPTY_ORDERS_VOLUME);
      setAppFeedbackSummary(dd.appFeedbackSummary ?? EMPTY_APP_FEEDBACK_SUMMARY);

    } catch (e) {
      console.error('ERROR - INIT HOME SCREEN:', e);

    } finally {
      if (showLoading) setLoading(false);
    }
  }

  useEffect(() => {
    fetchDashboardData(true);

    // Os numeros do dashboard sao buscados uma unica vez ao montar a tela. Se o admin registrar
    // uma acao em outra aba/app (ex.: enviar uma avaliacao) e so voltar para essa aba ja aberta,
    // os cards ficariam desatualizados ate um F5 manual. Refaz a busca (silenciosamente) sempre
    // que a aba do Home recupera o foco.
    const handleFocus = () => fetchDashboardData(false);
    window.addEventListener('focus', handleFocus);

    return () => window.removeEventListener('focus', handleFocus);

  }, [])


  const handleManualRefresh = async () => {
    setRefreshing(true);
    await fetchDashboardData(false);
    setRefreshing(false);
  }




  const ContentBox = ({ title, content }) => {
    return (
      <div className={styles.contentBox}>
        <div className={styles.contentBoxHeader}>
          <span className={styles.contentBoxHeaderTitle}>{title}</span>
        </div>

        <div className={styles.contentBoxContainer}>{content}</div>
      </div>
    );
  };




  // CARD DE ESTATISTICA — O TOOLTIP (HOVER) SUBSTITUI O ANTIGO CLIQUE-PARA-ABRIR-ALERTA,
  // SEGUINDO O MESMO PADRAO JA USADO EM OUTRAS TELAS DO ADMIN (ex.: clientsList, ifoodRequests)
  const StaticsCard = ({ title, value, subtitle, message, loading }) => {
    const card = (
      <div className={styles.staticsCard}>
        <div className={styles.cardTitleArea}>
          <FiHelpCircle size={14} color="#888" />
          <h2 className={styles.cardTitle}>{title}</h2>
        </div>

        <div className={styles.cardContent}>
          {loading ? (
            <Oval
              visible={true}
              height="24"
              width="24"
              color="#2eb4f1"
              ariaLabel="oval-loading"
            />

          ) : (
            <div className={styles.cardValueArea}>
              <h3 className={styles.cardValue}>{value}</h3>
              {subtitle && (
                <span className={styles.cardSubtitle}>{subtitle}</span>
              )}
            </div>
          )}
        </div>
      </div>
    );

    if (!message) return card;

    return (
      <Tooltip title={message} arrow placement="top">
        {card}
      </Tooltip>
    );
  };



  // TITULO DE SECAO, USADO PARA AGRUPAR OS CARDS POR TEMA (FINANCEIRO / CRESCIMENTO / OPERACAO)
  const SectionTitle = ({ title }) => (
    <h2 className={styles.sectionTitle}>{title}</h2>
  );



  // MONTA OS DADOS DO GRAFICO DE PIZZA A PARTIR DO BREAKDOWN DE STATUS DE COBRANCA
  const billingStatusChartData = BILLING_STATUS_CHART_META
    .map((meta, index) => ({
      id: index,
      value: billingStatusBreakdown[meta.key] ?? 0,
      label: meta.label,
      color: meta.color,
    }))
    .filter((item) => item.value > 0);

  const netGrowthLabel = `${netGrowth.netGrowth > 0 ? "+" : ""}${netGrowth.netGrowth}`;

  const appFeedbackValue = appFeedbackSummary.totalFeedback > 0
    ? `${appFeedbackSummary.positivePercent}% positivo`
    : "Sem feedback";

  const appFeedbackSubtitle = appFeedbackSummary.totalFeedback > 0
    ? `${appFeedbackSummary.totalFeedback} feedback(s) no período`
    : "Nenhum nos últimos 30 dias";



  // SCREEN ==================================================================>

  return (
    <>
      <Header>
        <h2 style={{ color: "#fff" }}>Home</h2>

        {/* <button
          className={styles.settingsButton}
          onClick={handleManualRefresh}
          title="Atualizar dados"
          disabled={refreshing}
        >
          <FiRefreshCw size={20} color="#005586" className={refreshing ? styles.spinningIcon : undefined} />
        </button> */}

        <button
          className={styles.settingsButton}
          onClick={() => setSettingsModalOpen(true)}
          title="Configurações"
        >
          <FiSettings size={22} color="#005586" />
        </button>
      </Header>

      <div className={styles.container}>

        <SectionTitle title="Financeiro" />
        <div className={styles.staticsArea}>
          <StaticsCard title="Receita mensal" loading={loading} value={toCurrency(monthlyRevenue)} message='Mostra a receita mensal recorrente contratada (MRR): soma das mensalidades dos restaurantes parceiros ativos, incluindo os em teste que já têm mensalidade definida. Empresas excluídas, bloqueadas, inadimplentes ou sem mensalidade não entram.'/>
          <StaticsCard title="Ticket médio" loading={loading} value={toCurrency(averageTicket)} message='Mostra o ticket médio por restaurante parceiro, dividindo a receita mensal pelo número de empresas que a compõem.'/>
          <StaticsCard
            title="Receita inadimplente"
            loading={loading}
            value={toCurrency(atRiskRevenue)}
            subtitle={`${billingStatusBreakdown.OVERDUE} empresa(s)`}
            message='Soma da mensalidade das empresas ativas e atualmente inadimplentes (pagamento em atraso). Nao inclui empresas em trial (ainda nao sao cobradas) nem empresas ja excluidas do sistema.'
          />
        </div>

        <SectionTitle title="Crescimento" />
        <div className={styles.staticsArea}>
          <StaticsCard title="Clientes ativos" loading={loading} value={activeClients} message='Mostra a quantidade de restaurantes ativos no momento atual.'/>
          <StaticsCard
            title="Crescimento líquido"
            loading={loading}
            value={netGrowthLabel}
            subtitle={`+${netGrowth.newThisMonth} nova(s) / ${netGrowth.churnedThisMonth} excluída(s)`}
            message={`Saldo de empresas no mês atual: ${netGrowth.newThisMonth} nova(s) menos ${netGrowth.churnedThisMonth} excluida(s). Um saldo negativo significa que mais empresas sairam do que entraram neste mês - e um número valido, não um erro.`}
          />
          <StaticsCard
            title="Conversão de trial"
            loading={loading}
            value={`${trialConversion.conversionRate}%`}
            subtitle={`${trialConversion.converted} de ${trialConversion.converted + trialConversion.lost} finalizados`}
            message={`De ${trialConversion.converted + trialConversion.lost} trials finalizados (por conversão ou por vencimento do prazo), ${trialConversion.converted} viraram clientes pagantes. ${trialConversion.ongoing} trial(s) ainda dentro do prazo.`}
          />
          <StaticsCard
            title="Leads no mês"
            loading={loading}
            value={leadsSummary.leadsThisMonth}
            subtitle={`${leadsSummary.convertedLeads} de ${leadsSummary.totalLeads} convertidos (histórico)`}
            message={`${leadsSummary.leadsThisMonth} lead(s) captado(s) neste mês. Do total histórico de ${leadsSummary.totalLeads} leads, ${leadsSummary.convertedLeads} já converteram em clientes.`}
          />
        </div>

        <SectionTitle title="Operação" />
        <div className={styles.staticsArea}>
          <StaticsCard
            title="Empresas em trial"
            loading={loading}
            value={billingStatusBreakdown.TRIALING}
            subtitle="Dentro do período de teste"
            message="Quantidade de empresas atualmente dentro do período de trial: ainda não converteram para plano pago e o prazo de teste ainda não venceu."
          />
          <StaticsCard
            title="Pedidos hoje"
            loading={loading}
            value={ordersVolume.ordersToday}
            subtitle={`${ordersVolume.ordersLast7Days} nos últimos 7 dias`}
            message={`Quantidade de pedidos processados na plataforma inteira hoje. Nos últimos 7 dias, foram ${ordersVolume.ordersLast7Days} pedidos no total.`}
          />
          <StaticsCard
            title="Feedback do app (30d)"
            loading={loading}
            value={appFeedbackValue}
            subtitle={appFeedbackSubtitle}
            message={
              appFeedbackSummary.totalFeedback > 0
                ? `${appFeedbackSummary.goodCount} positivo(s), ${appFeedbackSummary.mediumCount} neutro(s) e ${appFeedbackSummary.badCount} negativo(s), com base em ${appFeedbackSummary.totalFeedback} feedback(s) enviados sobre o sistema SofChef nos últimos 30 dias.`
                : 'Não há feedback sobre o sistema registrado nos últimos 30 dias.'
            }
          />
        </div>

        <div className={styles.content}>
          <div className={styles.contentRowArea}>
            {loading ? (
              <div className={styles.chartsLoadingArea}>
                <Oval visible={true} height="40" width="40" color="#2eb4f1" ariaLabel="oval-loading" />
              </div>

            ) : (
              <>
                <ContentBox
                  title={"Empresas por status de cobrança"}
                  content={
                    billingStatusChartData.length > 0 ? (
                      <PieChart
                        series={[
                          {
                            data: billingStatusChartData,
                            innerRadius: 50,
                            outerRadius: 100,
                            paddingAngle: 2,
                            cornerRadius: 4,
                          },
                        ]}
                        height={330}
                        slotProps={{
                          legend: { hidden: false },
                        }}
                      />
                    ) : (
                      <p style={{ textAlign: "center", padding: "40px 0" }}>Sem empresas cadastradas.</p>
                    )
                  }
                />

                <ContentBox
                  title={"Receita confirmada (últimos 12 meses)"}
                  content={
                    <LineChart
                      height={300}
                      xAxis={[{ scaleType: "point", data: revenueHistory.map((item) => item.month) }]}
                      series={[
                        {
                          data: revenueHistory.map((item) => item.total),
                          label: "Receita confirmada",
                          color: "#2eb4f1",
                          valueFormatter: (value) => toCurrency(value ?? 0),
                        },
                      ]}
                    />
                  }
                />
              </>
            )}
          </div>
        </div>

      </div>

      <SettingsModal isOpen={settingsModalOpen} onClose={() => setSettingsModalOpen(false)} />
    </>
  );
}
